import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Character } from '../services/api.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useStory } from '../contexts/StoryContext.tsx';
import { useCharacterImagesQuery } from '../hooks/useCharacterImagesQuery.ts';
import StorySpinner from '../components/StorySpinner.tsx';

// Extended interface for dual status tracking
interface CharacterWithStatus extends Character {
  description_status: 'generating' | 'completed';
  showDescription?: boolean;
}

// Helper to compute slot count intelligently
const computeSlotCount = (
  statusCount?: number,
  have = 0,
  currentStep?: string
) => {
  // Highest priority: backend-provided count
  if (typeof statusCount === 'number' && statusCount > 0) return statusCount;
  // Next: whatever we already have locally
  const derived = have;
  if (derived > 0) return derived;
  // While still in characters step but nothing yet, show 1 placeholder
  return currentStep === 'characters' ? 1 : 0;
};

const CharacterBuilder: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedStoryId, storyStatus } = useStory();
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterWithStatus | null>(null);
  
  const {
    data: charactersData = [],
    isLoading: imgsLoading,
    isFetching: imgsFetching,
    error: imgsError,
  } = useCharacterImagesQuery(selectedStoryId, storyStatus);

  // Convert query data to CharacterWithStatus for badges
  const characters: CharacterWithStatus[] = (charactersData || []).map((c) => ({
    ...c,
    description_status: c.description ? 'completed' : 'generating',
  }));

  // show spinner ONLY while actually creating a story (not while status is null)
  const isCreatingNow =
    !!storyStatus &&
    storyStatus.current_step === 'characters' &&
    storyStatus.character_phase === 'creating_story';

  // debounce to avoid flicker on quick route/story switches
  const [showCreationSpinner, setShowCreationSpinner] = useState(false);
  useEffect(() => {
    if (isCreatingNow) {
      const t = setTimeout(() => setShowCreationSpinner(true), 250); // 250–300ms feels good
      return () => clearTimeout(t);
    }
    setShowCreationSpinner(false);
  }, [isCreatingNow]);

  // Show a small, neutral spinner if we have no status and no data yet
  const [showHydrationSpinner, setShowHydrationSpinner] = useState(false);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;

    const noStatus = !storyStatus;
    const noData = characters.length === 0;

    // only show if truly nothing to render (and we're not in the creating spinner)
    const shouldShow = noStatus && noData && !isCreatingNow;

    if (shouldShow) {
      // tiny delay to avoid flicker on super-fast loads
      t = setTimeout(() => setShowHydrationSpinner(true), 120);
    } else {
      setShowHydrationSpinner(false);
    }

    return () => {
      if (t) clearTimeout(t);
    };
  }, [storyStatus, characters.length, isCreatingNow, selectedStoryId]);

  // Scroll to top when component mounts or story changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedStoryId]);



  // Polling is handled by React Query

  // Placeholder Card Component for missing characters
  const PlaceholderCard: React.FC<{ index: number }> = ({ index }) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden h-full flex flex-col">
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
          <div className="text-sm text-gray-500">Generating Character...</div>
          <span className="sr-only">Generating character image, please wait...</span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">Character {index + 1}</h3>
          <div className="flex flex-col space-y-1">
            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="w-full animate-pulse bg-gradient-to-r from-purple-400 to-blue-400 h-2"></div>
        </div>
      </div>
    </div>
  );

  // Placeholder Grid Component for character generation phase
  const PlaceholderGrid: React.FC<{ count: number }> = ({ count }) => (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Character Builder</h1>
            <p className="text-gray-600">Creating your story's characters...</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
            {Array.from({ length: count }, (_, index) => (
              <PlaceholderCard key={`placeholder-${index}`} index={index} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Character Card Component (VideoBuilder-style)
  const CharacterCard: React.FC<{
    character: CharacterWithStatus;
    onSelectCharacter: (character: CharacterWithStatus) => void;
  }> = ({ character, onSelectCharacter }) => {
    const [imageError, setImageError] = useState(false);
    useEffect(() => { setImageError(false); }, [character.image_url]);
    
    const isImageGenerating = !character.image_url || character.status === 'generating';
    const isDescriptionGenerating = character.description_status === 'generating';

    // Handle card click - always clickable (VideoBuilder-style)
    const handleCardClick = () => {
      onSelectCharacter(character);
    };

    return (
      <div 
        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 cursor-pointer h-full flex flex-col"
        onClick={handleCardClick}
      >
        <div className="aspect-square mb-4 rounded-t-xl overflow-hidden bg-gray-100 flex items-center justify-center relative">
          {character.image_url && !imageError ? (
            <img
              src={character.image_url}
              alt={character.name}
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400">
              {isImageGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                  <div className="text-sm">Generating Image...</div>
                  <div className="text-xs text-gray-400 mt-1">Click to view details</div>
                </>
              ) : (
                <div className="text-4xl">👤</div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800">{character.name}</h3>
            {/* Status Badges */}
            <div className="flex flex-col space-y-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                isImageGenerating 
                  ? 'bg-yellow-100 text-yellow-800 animate-pulse' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {isImageGenerating ? 'Image' : 'Image ✓'}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                isDescriptionGenerating 
                  ? 'bg-yellow-100 text-yellow-800 animate-pulse' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {isDescriptionGenerating ? 'Desc' : 'Desc ✓'}
              </span>
            </div>
          </div>
          
          {/* Progress indicator for generating characters */}
          {isImageGenerating && (
            <div className="mb-3">
              <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 h-1 rounded-full w-full animate-pulse"></div>
              </div>
            </div>
          )}

          {/* Description Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCharacter({ ...character, showDescription: true });
            }}
            className="w-full px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 mt-auto"
          >
            Description
          </button>
        </div>
      </div>
    );
  };

  // Handle character click (VideoBuilder-style - always clickable)
  const handleCharacterClick = (character: CharacterWithStatus) => {
    setSelectedCharacter(character);
  };

  // Close modal
  const closeModal = () => {
    setSelectedCharacter(null);
  };

  // Retry function for failed API calls
  const handleRetry = () => {
    if (!selectedStoryId) return;
    queryClient.invalidateQueries({ queryKey: ['character-images', selectedStoryId] });
  };

  // Error handling: prefer React Query error if present
  if (imgsError && characters.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Characters</h2>
          <p className="text-gray-600 mb-4">{imgsError.message || 'Failed to load characters'}</p>
          <button
            onClick={handleRetry}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Step-based flow control using storyStatus
  // 1) Big crafting spinner (only real generation)
  if (showCreationSpinner) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 CharacterBuilder: Creating story - showing spinner', {
        current_step: storyStatus?.current_step,
        character_phase: storyStatus?.character_phase
      });
    }
    return <StorySpinner size="large" className="pb-48" />;
  }

  // 2) Neutral hydration spinner (briefly, on refresh/switch before data)
  if (showHydrationSpinner) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-48">
        <div className="text-center text-gray-600" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3" />
          <p className="text-sm">Loading story…</p>
        </div>
      </div>
    );
  }

  // Character generation phase - show placeholders if no characters yet
  if (
    storyStatus &&
    storyStatus.current_step === 'characters' &&
    storyStatus.character_phase === 'generating_characters' &&
    characters.length === 0
  ) {
    const count = computeSlotCount(
      storyStatus.character_count,
      characters.length,
      storyStatus.current_step
    );
    return <PlaceholderGrid count={count} />;
  }


  // No story selected - show empty state with generate story button
  if (!selectedStoryId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-48">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Stories Yet</h2>
          <p className="text-gray-600 mb-6">Create your first magical story and bring your characters to life!</p>
          <button
            onClick={() => navigate('/story-setup')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
          >
            Generate Your First Story
          </button>
        </div>
      </div>
    );
  }


  // Calculate total slots based on story status
  const totalSlots = computeSlotCount(
    storyStatus?.character_count,
    characters.length,
    storyStatus?.current_step
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Characters Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-semibold text-gray-800">Characters</h1>
        </div>
        
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
          {Array.from({ length: totalSlots }).map((_, i) => {
            const char = characters[i];
            return char
              ? <CharacterCard key={char.id ?? `char-${char.name}-${i}`} character={char} onSelectCharacter={handleCharacterClick} />
              : <PlaceholderCard key={`placeholder-${i}`} index={i} />;
          })}
          </div>
          
        </div>
      </div>

      {/* Character Image Modal */}
      {selectedCharacter && !selectedCharacter.showDescription && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">{selectedCharacter.name}</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              <div className="text-center">
                {selectedCharacter.image_url ? (
                  <img
                    src={selectedCharacter.image_url}
                    alt={selectedCharacter.name}
                    className="max-w-full max-h-[60vh] object-contain mx-auto rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-medium">Generating Character Image...</p>
                      <p className="text-sm text-gray-500 mt-2">This character is being processed</p>
                    </div>
                  </div>
                )}
                
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {selectedCharacter && selectedCharacter.showDescription && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{selectedCharacter.name} - Description</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {selectedCharacter.description || 'No description available for this character.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterBuilder;